import { Menu, Plus } from 'lucide-react';
import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';

import { RudderstackBrandLogo } from '@/_shared/components/RudderstackBrandLogo';
import { Button } from '@/_shared/components/ui/button';
import { SidebarInset, SidebarProvider, useSidebar } from '@/_shared/components/ui/sidebar';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { FeatureFlagsProvider } from '@/_shared/feature-flags/FeatureFlagsContext';
import { ANALYTICS_EVENTS, trackClientEvent } from '@/_shared/lib/analytics';
import { routePatternOf } from '@/_shared/lib/route-pattern';

import { AppSidebar } from './AppSidebar';
import { SiblingStatusBanner } from './SiblingStatusBanner';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback: React.ReactNode;
  route: string;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, { hasError: boolean }> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('[ErrorBoundary]', error, errorInfo);
    // The constructor name only. The message and the component stack can both quote customer data,
    // so neither leaves the browser.
    trackClientEvent(ANALYTICS_EVENTS.clientErrorRaised, { route: this.props.route, errorName: error.name });
  }

  override render(): React.ReactNode {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

function MobileMenuButton(): React.ReactElement {
  const { toggleSidebar } = useSidebar();
  return <Button variant="tertiary" onClick={toggleSidebar} aria-label="Open navigation menu" Icon={<Menu />} />;
}

function ErrorFallback(): React.ReactElement {
  return (
    <div className="flex flex-1 items-center justify-center">
      <div className="text-center">
        <h2 className="text-lg font-semibold text-foreground">Something went wrong</h2>
        <p className="mt-1 text-sm text-muted-foreground">Try refreshing the page.</p>
      </div>
    </div>
  );
}

function LocationKeyedErrorBoundary({ children }: { children: React.ReactNode }): React.ReactElement {
  const location = useLocation();
  // Matched once per navigation, not once per render: the value is read only if the subtree throws.
  const route = React.useMemo(() => routePatternOf(location.pathname), [location.pathname]);
  return (
    <ErrorBoundary key={location.pathname} fallback={<ErrorFallback />} route={route}>
      {children}
    </ErrorBoundary>
  );
}

export function AppLayout(): React.ReactElement {
  const { wp } = useWorkspace();

  return (
    <FeatureFlagsProvider>
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset className="h-svh overflow-hidden">
          <div className="flex items-center justify-between border-b border-border bg-background/70 px-3 py-3 backdrop-blur md:hidden">
            <MobileMenuButton />
            <RudderstackBrandLogo variant="full" className="h-5" />
            <Button variant="tertiary" iconOnly asChild>
              <Link to={wp()} aria-label="New chat">
                <Plus className="size-4" />
              </Link>
            </Button>
          </div>
          {/* ACT2-1124: the sibling status banner of a sibling workspace; renders nothing elsewhere. */}
          <SiblingStatusBanner />
          <div className="flex min-h-0 flex-1 overflow-hidden bg-grey-0">
            <LocationKeyedErrorBoundary>
              <Outlet />
            </LocationKeyedErrorBoundary>
          </div>
        </SidebarInset>
      </SidebarProvider>
    </FeatureFlagsProvider>
  );
}
